import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';

const LEADS_PER_PAGE = 20;

const useLeads = (shouldFetch = true, filters = {}, sortConfig = { field: 'created_at', direction: 'desc' }) => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editedDetails, setEditedDetails] = useState({});
  const [editingTasks, setEditingTasks] = useState(null);
  const [editingProviders, setEditingProviders] = useState(null);
  const [commonTaskNames, setCommonTaskNames] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [allTaskNames, setAllTaskNames] = useState([]);
  const [appliedPriorityFilter, setAppliedPriorityFilter] = useState(null);
  const [filteredLeadCount, setFilteredLeadCount] = useState(0);
  const [fetchMultiplier, setFetchMultiplier] = useState(1);
  const [debugInfo, setDebugInfo] = useState({
    priorityFilterValue: null,
    leadsFound: 0,
    leadsWithPriorities: []
  });

  // Fetch all task names once on component mount
  useEffect(() => {
    if (shouldFetch) {
      const fetchAllTaskNames = async () => {
        try {
          const { data, error } = await supabase
            .from('patient_cases')
            .select('tasks');
            
          if (error) throw error;
          
          const taskSet = new Set();
          data.forEach(item => {
            if (item.tasks && Array.isArray(item.tasks)) {
              item.tasks.forEach(task => {
                if (task.name && task.name.trim() !== '' && task.name !== 'custom') {
                  taskSet.add(task.name);
                }
              });
            }
          });
          
          setAllTaskNames(Array.from(taskSet).sort());
        } catch (error) {
          console.error('Error fetching all task names:', error);
        }
      };
      
      fetchAllTaskNames();
    }
  }, [shouldFetch]);

  // Extract common task names from leads
  useEffect(() => {
    if (leads.length > 0) {
      const taskNames = new Set();
      leads.forEach(lead => {
        lead.tasks?.forEach(task => {
          if (task.name && task.name !== 'custom') {
            taskNames.add(task.name);
          }
        });
      });
      setCommonTaskNames(Array.from(taskNames).filter(name => name.trim() !== '').sort());
    }
  }, [leads]);

  // Reset pagination when filters change
  useEffect(() => {
    if (shouldFetch) {
      setPage(0);
      
      // Adjust fetch multiplier based on priority filter
      // This helps fetch more leads when we know some will be filtered out client-side
      if (filters.priority && filters.priority !== 'all') {
        setAppliedPriorityFilter(filters.priority);
        // We don't need a multiplier anymore since we can filter by priority directly in the database
        setFetchMultiplier(1);
      } else {
        setAppliedPriorityFilter(null);
        setFetchMultiplier(1);
      }
      
      fetchLeads(0, filters, sortConfig);
    }
  }, [shouldFetch, JSON.stringify(filters), JSON.stringify(sortConfig)]);
  
  // Helper function to get highest priority for a lead's tasks (for sorting/display)
  const getHighestPriority = (tasks) => {
    if (!tasks?.length) return 0;
    
    // Extract priority values, parse them as integers, and find the maximum
    const priorities = tasks.map(t => {
      // Handle different formats of priority (string or number)
      const priority = t.priority;
      if (typeof priority === 'string') {
        return parseInt(priority, 10) || 0;
      }
      return priority || 0;
    });
    
    return Math.max(...priorities);
  };

  // Fetch leads with pagination and filters applied at database level
  const fetchLeads = useCallback(async (pageNumber, currentFilters, currentSortConfig) => {
    try {
      setIsLoading(true);
      setError(null);

      // Determine if we're using priority filtering
      const hasPriorityFilter = currentFilters?.priority && currentFilters.priority !== 'all';
      let priorityFilterValue = null;
      
      if (hasPriorityFilter) {
        // Store the priority filter value for debugging
        priorityFilterValue = currentFilters.priority;
        console.log(`Priority filter active: ${priorityFilterValue}`);
      }
      
      const from = pageNumber * LEADS_PER_PAGE;
      const to = from + LEADS_PER_PAGE - 1;
      
      // Start building the query
      let query = supabase
        .from('patient_cases')
        .select('*', { count: 'exact' });
      
      // Apply filters at the database level
      if (currentFilters) {
        // Text search filter
        if (currentFilters.searchText) {
          const searchTerm = `%${currentFilters.searchText.toLowerCase()}%`;
          query = query.or(
            `first_name.ilike.${searchTerm},` +
            `last_name.ilike.${searchTerm},` +
            `email.ilike.${searchTerm},` +
            `phone.ilike.${searchTerm},` +
            `condition.ilike.${searchTerm},` +
            `details.ilike.${searchTerm}`
          );
        }
        
        // Status filter
        if (currentFilters.status && currentFilters.status !== 'all') {
          query = query.eq('status', currentFilters.status);
        }
        
        // Priority filter - Apply directly at database level
        if (hasPriorityFilter) {
          const priorityValue = parseInt(currentFilters.priority, 10);
          query = query.eq('priority', priorityValue);
        }
        
        // Date range filters for created_at
        if (currentFilters.dateRange?.start) {
          query = query.gte('created_at', currentFilters.dateRange.start);
        }
        
        if (currentFilters.dateRange?.end) {
          // Add time to include the entire day
          const endDate = new Date(currentFilters.dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          query = query.lte('created_at', endDate.toISOString());
        }
        
        // Date range filters for incident_date
        if (currentFilters.incidentDateRange?.start) {
          query = query.gte('incident_date', currentFilters.incidentDateRange.start);
        }
        
        if (currentFilters.incidentDateRange?.end) {
          // Add time to include the entire day
          const endDate = new Date(currentFilters.incidentDateRange.end);
          endDate.setHours(23, 59, 59, 999);
          query = query.lte('incident_date', endDate.toISOString());
        }
        
        // Task filters - handle each task filter separately to properly use the contains operator
        if (currentFilters.specificTasks && currentFilters.specificTasks.length > 0) {
          // We need to apply each task filter separately and chain them
          for (const taskFilter of currentFilters.specificTasks) {
            // Construct a containment filter using PostgreSQL specific syntax
            const jsonQuery = `[{"name":"${taskFilter.name}","complete":${taskFilter.complete}}]`;
            
            // Apply the filter using Supabase's containment operator
            query = query.filter('tasks', 'cs', jsonQuery);
          }
        }

        // Ref filter - handle key-value pairs in the 'ref' JSONB column
        if (currentFilters.refFilter && currentFilters.refFilter.key) {
          const refKey = currentFilters.refFilter.key;
          const refValue = currentFilters.refFilter.value;

          // Log the values being used for the ref filter
          console.log(`Applying ref filter: Key='${refKey}', Value='${refValue}'`);

          if (refValue) {
            // Use Supabase's contains filter for JSONB containment
            console.log(`DEBUG: Using Supabase's contains filter for JSONB`);
            
            // Create a simple JSON object with our key-value pair
            const searchObject = {};
            searchObject[refKey] = refValue;
            
            console.log(`DEBUG: Searching for JSON: ${JSON.stringify(searchObject)}`);
            
            // Use the contains filter which translates to @> in PostgreSQL
            query = query.contains('ref', searchObject);
          } else {
            // For key-only filtering, try with a null value
            console.log(`DEBUG: Looking for key existence using contains with null`);
            
            // Create object with null value to check key existence
            const keyOnlyObject = {};
            keyOnlyObject[refKey] = null;
            
            // First try to find records with any value for this key
            query = query.or('ref', 'cs', `{\"${refKey}\":\"\"}`);
          }
        }
      }
      
      // Apply sorting
      if (currentSortConfig) {
        const { field, direction } = currentSortConfig;
        // For standard fields we can sort directly
        if (['created_at', 'updated_at', 'incident_date', 'first_name', 'last_name', 'email', 'phone', 'condition', 'status', 'priority'].includes(field)) {
          query = query.order(field, { ascending: direction === 'asc' });
        } else if (field === 'tasks') {
          // For task count sorting, use created_at as fallback
          query = query.order('created_at', { ascending: direction === 'asc' });
        }
      }
      
      // Apply pagination
      query = query.range(from, to);
      
      // Execute query with pagination
      console.log('DEBUG: About to execute query:', query.url ? query.url.toString() : 'No URL available');
      const { data, error, count } = await query;

      if (error) {
        console.error('DEBUG: Query error:', error);
        throw error;
      }
      
      console.log(`DEBUG: Query returned ${data ? data.length : 0} results out of ${count} total matches`);
      
      // Debug: Log the first few records to see the structure of the ref field
      if (data && data.length > 0) {
        console.log('Sample record ref field structure:');
        const sampleRecords = data.slice(0, 3); // Take up to 3 records
        sampleRecords.forEach((record, i) => {
          console.log(`Record ${i+1} ref:`, record.ref);
        });
      }
      
      // Update total count of all matching records
      if (count !== undefined) {
        setTotalCount(count);
        setFilteredLeadCount(count);
      }

      // For debugging, collect information about lead priorities
      if (hasPriorityFilter) {
        console.log('Leads with priority', priorityFilterValue, ':', data.length);
        
        // Store debug info
        setDebugInfo({
          priorityFilterValue,
          leadsFound: data.length,
          leadsWithPriorities: data.map(lead => ({
            id: lead.id,
            priority: lead.priority,
            tasks: lead.tasks?.map(t => ({
              name: t.name,
              priority: t.priority,
              complete: t.complete
            }))
          }))
        });
      }
      
      // Handle task count sorting if needed
      let sortedData = [...data];
      if (currentSortConfig && currentSortConfig.field === 'tasks') {
        sortedData.sort((a, b) => {
          const aValue = a.tasks?.filter(t => !t.complete).length || 0;
          const bValue = b.tasks?.filter(t => !t.complete).length || 0;
          
          if (aValue < bValue) return currentSortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return currentSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });
      }
      
      // Update leads state
      if (pageNumber === 0) {
        setLeads(sortedData);
      } else {
        setLeads(prev => [...prev, ...sortedData]);
      }

      // Determine if there's more data to fetch
      const loadedCount = pageNumber * LEADS_PER_PAGE + sortedData.length;
      setHasMore(loadedCount < count);
      
    } catch (error) {
      console.error('Error fetching leads:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLeads(nextPage, filters, sortConfig);
    }
  }, [isLoading, hasMore, page, fetchLeads, filters, sortConfig]);

  // Helper to check if we're currently using priority filtering
  const isPriorityFilterActive = () => {
    return filters.priority && filters.priority !== 'all';
  };

  // Update functions
  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('patient_cases')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      
      setLeads(leads.map(lead => 
        lead.id === id ? { ...lead, status: newStatus } : lead
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const updateDetails = async (id) => {
    try {
      const newDetails = editedDetails[id];
      if (newDetails === undefined) return;

      const { error } = await supabase
        .from('patient_cases')
        .update({ details: newDetails })
        .eq('id', id);

      if (error) throw error;
      
      setLeads(leads.map(lead => 
        lead.id === id ? { ...lead, details: newDetails } : lead
      ));

      setEditedDetails(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    } catch (error) {
      console.error('Error updating details:', error);
    }
  };

  const handleDetailsChange = (id, value) => {
    setEditedDetails(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const updateTasks = async (id, newTasks) => {
    try {
      const { error } = await supabase
        .from('patient_cases')
        .update({ tasks: newTasks })
        .eq('id', id);

      if (error) throw error;
      
      setLeads(leads.map(lead => 
        lead.id === id ? { ...lead, tasks: newTasks } : lead
      ));
    } catch (error) {
      console.error('Error updating tasks:', error);
    }
  };

  const toggleTaskComplete = (leadId, taskIndex) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead?.tasks) return;

    const newTasks = [...lead.tasks];
    newTasks[taskIndex] = {
      ...newTasks[taskIndex],
      complete: !newTasks[taskIndex].complete
    };
    updateTasks(leadId, newTasks);
  };

  const addTask = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    const newTask = {
      name: "",
      complete: false,
      priority: 1
    };
    const newTasks = [...(lead.tasks || []), newTask];
    setEditingTasks({ leadId, tasks: newTasks });
    
    // Focus the task name dropdown after a short delay to allow the component to render
    setTimeout(() => {
      const taskSelect = document.querySelector(`[data-lead-id="${leadId}"] select`);
      if (taskSelect) {
        taskSelect.focus();
      }
    }, 10);
  };

  const saveTaskChanges = (leadId) => {
    const taskData = editingTasks?.tasks
      .filter(task => {
        // Filter out empty tasks or custom tasks without names
        if (!task.name) return false;
        if (task.name === 'custom' && (!task.customName || !task.customName.trim())) return false;
        return true;
      })
      .map(task => ({
        ...task,
        name: task.name === 'custom' ? 'custom' : task.name,
        customName: task.name === 'custom' ? task.customName.trim() : undefined
      })) || [];
      
    updateTasks(leadId, taskData);
    setEditingTasks(null);
  };

  const updateProviders = async (id, newProviders) => {
    try {
      // Don't update if there are no changes
      const lead = leads.find(l => l.id === id);
      if (!lead) return;
      
      // Ensure each provider has the correct structure
      const formattedProviders = (newProviders || []).map(provider => {
        // If provider is already in correct format, return it
        if (typeof provider === 'object' && provider !== null) {
          return {
            name: provider.name || '',
            phone: provider.phone || '',
            fax: provider.fax || '',
            email: provider.email || ''
          };
        }
        // If provider is a string (from legacy data), convert to new format
        return {
          name: provider || '',
          phone: '',
          fax: '',
          email: ''
        };
      });
      
      // Filter out empty providers (with no name)
      const filteredProviders = formattedProviders.filter(p => p.name.trim() !== '');
      
      const { error } = await supabase
        .from('patient_cases')
        .update({ providers: filteredProviders })
        .eq('id', id);

      if (error) throw error;
      
      setLeads(leads.map(lead => 
        lead.id === id ? { ...lead, providers: filteredProviders } : lead
      ));
      setEditingProviders(null);
    } catch (error) {
      console.error('Error updating providers:', error);
    }
  };

  const updateLeadPriority = async (id, newPriority) => {
    try {
      // Convert newPriority to integer
      const priorityValue = parseInt(newPriority, 10);
      
      // Update the lead's priority directly
      const { error } = await supabase
        .from('patient_cases')
        .update({ priority: priorityValue })
        .eq('id', id);

      if (error) throw error;
      
      // Update the local state
      setLeads(leads.map(lead => 
        lead.id === id ? { ...lead, priority: priorityValue } : lead
      ));
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  return {
    leads,
    isLoading,
    error,
    hasMore,
    loadMore,
    totalCount: filteredLeadCount,
    commonTaskNames,
    allTaskNames,
    updateStatus,
    updateLeadPriority,
    getHighestPriority,
    toggleTaskComplete,
    updateDetails,
    handleDetailsChange,
    editedDetails,
    setEditedDetails,
    editingTasks,
    setEditingTasks,
    addTask,
    saveTaskChanges,
    updateProviders,
    editingProviders,
    setEditingProviders,
    debugInfo
  };
};

export default useLeads; 